import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Home,
  Package,
  Upload,
  Route as RouteIcon,
  Boxes,
  PenTool,
  FileCode2,
  Users,
  Settings,
  Factory,
  LifeBuoy,
  ChevronsLeft,
  ChevronsRight,
  User,
  LogOut,
  Sun,
  Moon,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import useTheme from '../hooks/useTheme';
import api from '../services/api';
import './Sidebar.css';

const COLLAPSE_STORAGE_KEY = 'cw_sidebar_collapsed';

// Toggle claro/escuro oculto por padrão (ver frontend/.env.example e
// CLAUDE.md). Com a flag desligada, index.html trava o tema em dark
// ignorando localStorage — aqui só escondemos o controle.
const SHOW_THEME_TOGGLE = process.env.REACT_APP_SHOW_THEME_TOGGLE === 'true';

const NAV_GROUPS = [
  {
    label: 'Produção',
    items: [
      { to: '/', label: 'Início', icon: Home, end: true },
      { to: '/pecas', label: 'Produtos', icon: Package, end: true },
      { to: '/pecas/importar', label: 'Importar', icon: Upload },
      { to: '/roteiro', label: 'Roteiro', icon: RouteIcon },
      { to: '/paletizacao', label: 'Paletização', icon: Boxes },
    ],
  },
  {
    label: 'Ferramentas',
    items: [
      { to: '/editor', label: 'Editor MPR', icon: PenTool },
      { to: '/conversor-step', label: 'Conversor STEP', icon: FileCode2 },
    ],
  },
];

const ADMIN_GROUP = {
  label: 'Administração',
  items: [
    { to: '/admin/usuarios', label: 'Usuários', icon: Users },
    { to: '/admin/operacoes', label: 'Operações', icon: Settings },
    { to: '/admin/setores', label: 'Setores', icon: Factory },
  ],
};

function NavItem({ to, label, icon: Icon, end, collapsed }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) => `cw-sidebar-item${isActive ? ' cw-sidebar-item--active' : ''}`}
      aria-label={collapsed ? label : undefined}
      title={collapsed ? label : undefined}
    >
      <Icon size={18} strokeWidth={2} aria-hidden="true" />
      <span className="cw-sidebar-item-label">{label}</span>
    </NavLink>
  );
}

function Sidebar() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSE_STORAGE_KEY) === 'true'
  );
  const [menuAberto, setMenuAberto] = useState(false);
  const menuRef = useRef(null);

  // Versão de produto buscada do backend (GET /version) — não de env de
  // build do CRA, que congelaria no `npm run build` e poderia divergir do
  // backend rodando. A versão que importa pra métrica/auditoria é a do
  // backend em execução; o rodapé mostra exatamente essa. Ver CLAUDE.md
  // (kit de versionamento).
  const [versao, setVersao] = useState(null);

  useEffect(() => {
    let ativo = true;
    api
      .get('/version')
      .then((res) => {
        if (ativo) setVersao(res.data?.version ?? null);
      })
      .catch((err) => console.error('Sidebar: falha ao buscar /version:', err));
    return () => {
      ativo = false;
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(COLLAPSE_STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  useEffect(() => {
    if (!menuAberto) return undefined;
    const handleClickFora = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuAberto(false);
      }
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape') setMenuAberto(false);
    };
    document.addEventListener('mousedown', handleClickFora);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickFora);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [menuAberto]);

  const groups = user?.is_superuser ? [...NAV_GROUPS, ADMIN_GROUP] : NAV_GROUPS;

  return (
    <aside className={`cw-sidebar${collapsed ? ' cw-sidebar--collapsed' : ''}`}>
      <div className="cw-sidebar-top">
        <span className="cw-sidebar-logo">{collapsed ? 'CW' : 'CoreWood'}</span>
        <button
          type="button"
          className="cw-sidebar-collapse-btn"
          onClick={() => setCollapsed((v) => !v)}
          aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
          title={collapsed ? 'Expandir menu' : 'Recolher menu'}
        >
          {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
        </button>
      </div>

      <nav className="cw-sidebar-nav">
        {groups.map((group) => (
          <div className="cw-sidebar-group" key={group.label}>
            {!collapsed && <div className="cw-sidebar-group-label">{group.label}</div>}
            {group.items.map((item) => (
              <NavItem key={item.to} {...item} collapsed={collapsed} />
            ))}
          </div>
        ))}
      </nav>

      <div className="cw-sidebar-footer">
        <NavItem to="/suporte" label="Suporte" icon={LifeBuoy} collapsed={collapsed} />

        {SHOW_THEME_TOGGLE && (
          <button
            type="button"
            className="cw-sidebar-theme-btn"
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? 'Ativar tema claro' : 'Ativar tema escuro'}
            title={collapsed ? (theme === 'dark' ? 'Ativar tema claro' : 'Ativar tema escuro') : undefined}
          >
            {theme === 'dark' ? (
              <Sun size={18} strokeWidth={2} aria-hidden="true" />
            ) : (
              <Moon size={18} strokeWidth={2} aria-hidden="true" />
            )}
            <span className="cw-sidebar-item-label">{theme === 'dark' ? 'Tema claro' : 'Tema escuro'}</span>
          </button>
        )}

        <div className="cw-sidebar-user" ref={menuRef}>
          <button
            type="button"
            className="cw-sidebar-user-btn"
            onClick={() => setMenuAberto((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={menuAberto}
            aria-label={collapsed ? user?.username : undefined}
            title={collapsed ? user?.username : undefined}
          >
            <User size={18} strokeWidth={2} aria-hidden="true" />
            <span className="cw-sidebar-item-label">{user?.username}</span>
          </button>

          {menuAberto && (
            <div className="cw-sidebar-user-menu" role="menu">
              <button
                type="button"
                role="menuitem"
                className="cw-sidebar-user-menu-item"
                onClick={() => { setMenuAberto(false); navigate('/profile'); }}
              >
                <User size={15} aria-hidden="true" /> Perfil
              </button>
              <button
                type="button"
                role="menuitem"
                className="cw-sidebar-user-menu-item"
                onClick={() => { setMenuAberto(false); logout(); }}
              >
                <LogOut size={15} aria-hidden="true" /> Sair
              </button>
            </div>
          )}
        </div>

        {versao && (
          <div
            className="cw-sidebar-version"
            title={`CoreWood versão ${versao}`}
          >
            {collapsed ? versao : `v${versao}`}
          </div>
        )}
      </div>
    </aside>
  );
}

export default Sidebar;
